import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import AuthenticatedFileButton from '../../components/AuthenticatedFileButton';
import prestacaoContaService from '../../services/prestacaoContaService';
import relatorioService, { type PrestacaoMetaSelecao } from '../../services/relatorioPrestacaoContasService';
import ComprovanteForm from './ComprovanteForm';
import {
  bodyTextClass, dangerTextButtonClass, fonteRecursoLabel, formatCurrency, formatDate, labelTextClass,
  secondaryButtonClass, strongTextClass, surfaceClass, type SectionProps,
} from './shared';

type Props = SectionProps & { podeAdicionarComprovante: boolean; onAlterado?: () => void };

/**
 * Anexo 2 -- seleção das metas executadas nesta PC e, para cada uma, o
 * detalhamento por item ("etapa") com o valor executado no período. Alimenta
 * o "Valor utilizado" da Execução Financeira desta PC. Cada etapa tem sua
 * própria ação de anexar o comprovante de pagamento correspondente.
 */
const PrestacaoMetasExecucaoSection: React.FC<Props> = ({ pcId, podeEditar, podeAdicionarComprovante, onAlterado }) => {
  const { user } = useAuth();
  const role = String((user as any)?.role || (user as any)?.perfil || '').toUpperCase();
  const isOperador = role === 'OPERADOR' || role === 'ADMIN';

  const [metas, setMetas] = useState<PrestacaoMetaSelecao[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [comprovanteAberto, setComprovanteAberto] = useState<string | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (comprovanteAberto) dialog.current?.showModal();
  }, [comprovanteAberto]);
  useEffect(() => { setComprovanteAberto(null); }, [pcId]);

  const carregar = async () => {
    setLoading(true);
    setError(null);
    try {
      const dados = await relatorioService.getMetasSelecionadas(pcId);
      setMetas(dados);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao carregar as metas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pcId]);

  const alternarMeta = async (metaId: string, selecionada: boolean) => {
    setSaving(metaId);
    setError(null);
    try {
      if (selecionada) {
        if (!confirm('Remover esta meta e todos os pagamentos e comprovantes vinculados às suas etapas desta PC?')) {
          setSaving(null);
          return;
        }
        await relatorioService.removerMeta(pcId, metaId);
      } else {
        await relatorioService.selecionarMeta(pcId, metaId);
      }
      await carregar();
      onAlterado?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao atualizar a meta.');
    } finally {
      setSaving(null);
    }
  };

  const excluirComprovante = async (comprovanteId: string) => {
    if (!confirm('Excluir este comprovante?')) return;
    setSaving(comprovanteId);
    setError(null);
    try {
      await prestacaoContaService.deletarComprovante(comprovanteId);
      await carregar();
      onAlterado?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao excluir comprovante.');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className={`text-lg font-semibold ${strongTextClass}`}>Metas e Etapas Executadas</h2>
        <p className={`text-sm ${labelTextClass}`}>
          Selecione as metas executadas nesta PC e cadastre os pagamentos de cada etapa.
          O valor executado no período corresponde à soma dos pagamentos cadastrados.
        </p>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <p className={`text-sm ${bodyTextClass}`}>Carregando...</p>
      ) : metas.length === 0 ? (
        <p className={`text-sm ${labelTextClass}`}>Nenhuma meta cadastrada no plano de trabalho.</p>
      ) : (
        <div className="divide-y divide-slate-200 dark:divide-slate-800">
          {metas.map((meta) => (
            <section key={meta.metaId} className="space-y-3 py-4 first:pt-0 last:pb-0">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={meta.selecionada}
                  disabled={!podeEditar || saving === meta.metaId}
                  onChange={() => alternarMeta(meta.metaId, meta.selecionada)}
                />
                <span>
                  <span className={`block font-semibold ${strongTextClass}`}>{meta.metaTitulo}</span>
                  <span className={`text-xs ${labelTextClass}`}>Valor total da meta: {formatCurrency(meta.metaValor)}</span>
                </span>
              </label>

              {meta.selecionada && (
                <div className="divide-y divide-slate-200 dark:divide-slate-800">
                  {meta.itens.length === 0 ? (
                    <p className={`text-xs ${labelTextClass}`}>Esta meta não possui itens (etapas) cadastrados no plano de trabalho.</p>
                  ) : (
                    <>
                      {meta.itens.map((item, itemIndex) => {
                        const itemTitulo = item.itemTitulo?.trim() || `Etapa ${itemIndex + 1}`;
                        return (
                          <div key={item.itemId} className="space-y-3 py-4 first:pt-1 last:pb-0">
                            <div className="grid grid-cols-1 items-center gap-2 sm:grid-cols-3">
                              <div className="sm:col-span-2">
                                <p className={`text-sm font-medium ${strongTextClass}`}>{itemTitulo}</p>
                                <p className={`text-xs ${labelTextClass}`}>Valor planejado: {formatCurrency(item.itemValor)}</p>
                              </div>
                              <div>
                                <label className={`block text-xs font-medium ${labelTextClass}`}>Valor executado no período</label>
                                <p className={`font-semibold ${strongTextClass}`}>{formatCurrency(item.valorExecutado ?? 0)}</p>
                              </div>
                            </div>

                            {item.comprovantes.length > 0 && (
                              <div className="divide-y divide-slate-200 border-l-2 border-slate-200 pl-3 dark:divide-slate-700 dark:border-slate-700">
                                {item.comprovantes.map((c) => (
                                  <div key={c.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2 text-xs">
                                    <span className={strongTextClass}>{formatCurrency(c.valor)}</span>
                                    <span>{c.credorNome || 'Credor não informado'} · {c.credorDocumento || '—'}</span>
                                    <span>{fonteRecursoLabel(c.fonteRecurso)}</span>
                                    <span>Documento {c.numeroDocumentoFiscal || '—'}</span>
                                    <span>Emissão: {formatDate(c.dataDocumentoFiscal)} · Pagamento: {formatDate(c.dataPagamento)}</span>
                                    <span>Transferência bancária: {c.numeroTransferencia || '—'}</span>
                                    {c.anexos.map((anexo) => (
                                      <AuthenticatedFileButton
                                        key={anexo.id}
                                        url={anexo.viewUrl || ''}
                                        fileName={anexo.nomeOriginal}
                                        disabled={!anexo.viewUrl}
                                        className={`${labelTextClass} underline`}
                                      >
                                        <span>{anexo.descricao ? `${anexo.descricao}: ` : ''}{anexo.nomeOriginal}</span>
                                      </AuthenticatedFileButton>
                                    ))}
                                    {isOperador && podeAdicionarComprovante && (
                                      <button type="button" onClick={() => excluirComprovante(c.id)} disabled={saving === c.id}
                                        className={dangerTextButtonClass}>
                                        Excluir
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            {isOperador && item.id && (
                              comprovanteAberto === item.id ? (
                                <dialog ref={dialog} onCancel={() => setComprovanteAberto(null)} aria-labelledby="pagamento-etapa-titulo"
                                  className={`w-[min(56rem,95vw)] max-h-[90vh] overflow-y-auto rounded-2xl border p-5 shadow-xl backdrop:bg-black/50 ${surfaceClass}`}>
                                  <h3 id="pagamento-etapa-titulo" className={`mb-4 text-lg font-semibold ${strongTextClass}`}>
                                    Pagamento da etapa — {itemTitulo}
                                  </h3>
                                  <ComprovanteForm
                                    pcId={pcId}
                                    itemExecucaoId={item.id}
                                    podeAdicionarComprovante={podeAdicionarComprovante}
                                    disabledMessage="Esta PC não aceita novos comprovantes neste momento."
                                    onAdicionado={() => { setComprovanteAberto(null); carregar(); onAlterado?.(); }}
                                  />
                                  <button type="button" onClick={() => setComprovanteAberto(null)}
                                    className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${secondaryButtonClass}`}>
                                    Cancelar
                                  </button>
                                </dialog>
                              ) : (
                                <button type="button" onClick={() => setComprovanteAberto(item.id)}
                                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${secondaryButtonClass}`}>
                                  + Adicionar comprovante desta etapa
                                </button>
                              )
                            )}
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default PrestacaoMetasExecucaoSection;
